import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { toast } from 'sonner';
import Header from './Header';
import CompanyNameOnboarding from './CompanyNameOnboarding';
import { formatDateTime } from '../utils/dateFormatter';
import { formatStatus } from '../utils/formatStatus';
import {
  Plus,
  CheckCircle,
  Clock,
  AlertCircle,
  TrendingUp,
  FileText,
  Calendar,
  Archive,
  ArchiveRestore,
  MoreVertical,
  Search,
  AlertTriangle,
  ShieldCheck,
  ArrowRight,
  Pin,
  PinOff,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const API = '/api';

const Dashboard = () => {
  const { user, currentWorkspace } = useAuth();
  const navigate = useNavigate();
  const [audits, setAudits] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active');
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState('archive');
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [auditSearch, setAuditSearch] = useState('');
  const [sortKey, setSortKey] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [pinnedIds, setPinnedIds] = useState([]);
  const [metricsMap, setMetricsMap] = useState({});

  const currentRole = currentWorkspace?.role || 'user';

  useEffect(() => {
    if (currentRole === 'employee') { navigate('/employee/portal'); return; }
    if (currentRole === 'auditor') { navigate('/auditor/dashboard'); return; }
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, currentWorkspace, navigate]);

  const fetchDashboardData = async () => {
    try {
      const [auditsResponse, statsResponse, pinsResponse] = await Promise.all([
        axios.get(`${API}/audits`),
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/dashboard/pins`).catch(() => ({ data: { audit_ids: [] } })),
      ]);
      setAudits(auditsResponse.data);
      setStats(statsResponse.data);
      const pins = pinsResponse.data?.audit_ids || [];
      const initialized = pinsResponse.data?.initialized;
      // only keep pins that still exist
      const existingIds = new Set((auditsResponse.data || []).map(a => a.id));
      let validPins = pins.filter(id => existingIds.has(id));

      // Auto-pin: first-time users with exactly one active audit get it pinned automatically.
      const activeAudits = (auditsResponse.data || []).filter(a => !a.archived_at);
      if (!initialized && validPins.length === 0 && activeAudits.length === 1) {
        try {
          const res = await axios.post(`${API}/dashboard/pins/${activeAudits[0].id}`);
          validPins = res.data.audit_ids || [activeAudits[0].id];
        } catch (e) { /* non-blocking */ }
      }

      setPinnedIds(validPins);
      validPins.forEach(loadMetrics);
    } catch (error) {
      toast.error('Failed to fetch dashboard data');
      console.error('Dashboard fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMetrics = async (auditId) => {
    try {
      const res = await axios.get(`${API}/audits/${auditId}/summary`);
      setMetricsMap(prev => ({ ...prev, [auditId]: res.data }));
    } catch (e) {
      setMetricsMap(prev => ({ ...prev, [auditId]: { error: true } }));
    }
  };

  const togglePin = async (e, auditId) => {
    if (e) e.stopPropagation();
    const isPinned = pinnedIds.includes(auditId);
    try {
      if (isPinned) {
        const res = await axios.delete(`${API}/dashboard/pins/${auditId}`);
        setPinnedIds(res.data.audit_ids || []);
        toast.success('Removed from Command Centre');
      } else {
        const res = await axios.post(`${API}/dashboard/pins/${auditId}`);
        setPinnedIds(res.data.audit_ids || []);
        if (!metricsMap[auditId]) loadMetrics(auditId);
        toast.success('Pinned to Command Centre');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update pin');
    }
  };

  const handleArchive = async (auditId) => {
    try {
      await axios.post(`${API}/audits/${auditId}/archive`);
      setAudits(prev => prev.map(a => a.id === auditId ? { ...a, archived_at: new Date().toISOString() } : a));
      toast.success('Audit archived');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to archive audit');
    }
  };

  const handleUnarchive = async (auditId) => {
    try {
      await axios.post(`${API}/audits/${auditId}/unarchive`);
      setAudits(prev => prev.map(a => a.id === auditId ? { ...a, archived_at: null } : a));
      toast.success('Audit unarchived');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unarchive audit');
    }
  };

  const pinnedAudits = audits.filter(a => pinnedIds.includes(a.id));

  const filteredAudits = audits
    .filter(audit => {
      if (activeTab === 'active') return !audit.archived_at;
      if (activeTab === 'archived') return !!audit.archived_at;
      return true;
    })
    .filter(audit => {
      const q = auditSearch.trim().toLowerCase();
      if (!q) return true;
      return (
        (audit.name || '').toLowerCase().includes(q) ||
        (audit.audit_ref || '').toLowerCase().includes(q) ||
        (audit.organization || '').toLowerCase().includes(q) ||
        (audit.description || '').toLowerCase().includes(q)
      );
    })
    .sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      let av; let bv;
      switch (sortKey) {
        case 'name': av = (a.name || '').toLowerCase(); bv = (b.name || '').toLowerCase(); break;
        case 'organization': av = (a.organization || '').toLowerCase(); bv = (b.organization || '').toLowerCase(); break;
        case 'status': av = (a.status || ''); bv = (b.status || ''); break;
        case 'progress': av = a.progress || 0; bv = b.progress || 0; break;
        default: av = new Date(a.created_at).getTime(); bv = new Date(b.created_at).getTime();
      }
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });

  const activeCount = audits.filter(a => !a.archived_at).length;
  const archivedCount = audits.filter(a => !!a.archived_at).length;

  useEffect(() => { setSelectedIds([]); }, [activeTab]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'name' || key === 'organization' ? 'asc' : 'desc');
    }
  };

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <ChevronsUpDown className="w-3.5 h-3.5 text-[#CBD5E1]" />;
    return sortDir === 'asc'
      ? <ChevronUp className="w-3.5 h-3.5 text-[#0F62FE]" />
      : <ChevronDown className="w-3.5 h-3.5 text-[#0F62FE]" />;
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const selectableIds = filteredAudits.map(a => a.id);
  const allSelected = selectableIds.length > 0 && selectableIds.every(id => selectedIds.includes(id));
  const toggleSelectAll = () => setSelectedIds(allSelected ? [] : selectableIds);

  const openBulkDialog = (action) => { setBulkAction(action); setBulkDialogOpen(true); };

  const handleBulkConfirm = async () => {
    if (selectedIds.length === 0) return;
    setBulkProcessing(true);
    try {
      const endpoint = bulkAction === 'archive' ? 'bulk-archive' : 'bulk-unarchive';
      const response = await axios.post(`${API}/audits/${endpoint}`, { ids: selectedIds });
      const count = bulkAction === 'archive' ? response.data.archived_count : response.data.unarchived_count;
      setAudits(prev => prev.map(a => selectedIds.includes(a.id)
        ? { ...a, archived_at: bulkAction === 'archive' ? new Date().toISOString() : null } : a));
      toast.success(`${count} audit(s) ${bulkAction === 'archive' ? 'archived' : 'unarchived'}`);
      setSelectedIds([]);
      setBulkDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${bulkAction} audits`);
    } finally {
      setBulkProcessing(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'in_progress': 'bg-[#EFF6FF] text-[#0F62FE] border-[#BFDBFE]',
      'completed': 'bg-[#ECFDF5] text-[#047857] border-[#A7F3D0]',
      'needs_attention': 'bg-[#FEF3C7] text-[#B45309] border-[#FCD34D]',
      'failed': 'bg-[#FEF2F2] text-[#B91C1C] border-[#FCA5A5]',
      'pending': 'bg-[#F9FAFB] text-[#6B7280] border-[#E5E7EB]',
    };
    return colors[status] || 'bg-[#F9FAFB] text-[#6B7280] border-[#E5E7EB]';
  };

  const renderPinnedCard = (audit, solo) => {
    const m = metricsMap[audit.id];
    const pct = Math.round(audit.progress || (m && m.total ? (m.completed / m.total) * 100 : 0));
    const C = 2 * Math.PI * 42;
    const ringSize = solo ? 'w-28 h-28' : 'w-24 h-24';
    const ring = (
      <div className={`relative ${ringSize}`}>
        <svg className={`${ringSize} -rotate-90`} viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="42" fill="none" stroke="#E5E9F2" strokeWidth="8" />
          <circle cx="50" cy="50" r="42" fill="none" stroke="#0F62FE" strokeWidth="8" strokeLinecap="round" strokeDasharray={C} strokeDashoffset={C * (1 - pct / 100)} style={{ transition: 'stroke-dashoffset 0.6s ease' }} />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={`${solo ? 'text-2xl' : 'text-xl'} font-bold text-[#0F172A] leading-none`}>{pct}%</span>
          <span className="text-[10px] text-[#94A3B8] uppercase tracking-wide mt-0.5">Complete</span>
        </div>
      </div>
    );
    const chips = (
      <div className={`grid grid-cols-3 gap-3 ${solo ? 'max-w-md' : ''}`}>
        <div className="rounded-xl border border-[#E5E9F2] p-3 text-center">
          <p className="text-lg font-bold text-[#047857] leading-none">{m && !m.error ? m.satisfied : '—'}</p>
          <p className="text-[10px] text-[#94A3B8] uppercase tracking-wide mt-1.5">Satisfied</p>
        </div>
        <div className="rounded-xl border border-[#E5E9F2] p-3 text-center">
          <p className="text-lg font-bold text-[#B45309] leading-none">{m && !m.error ? m.needMore : '—'}</p>
          <p className="text-[10px] text-[#94A3B8] uppercase tracking-wide mt-1.5">Need Evidence</p>
        </div>
        <div className="rounded-xl border border-[#E5E9F2] p-3 text-center">
          <p className="text-lg font-bold text-[#B91C1C] leading-none">{m && !m.error ? m.violations : '—'}</p>
          <p className="text-[10px] text-[#94A3B8] uppercase tracking-wide mt-1.5">Violations</p>
        </div>
      </div>
    );
    const meta = (
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <span className="font-mono text-xs text-[#94A3B8]">{audit.audit_ref || 'No ID'}</span>
          <Badge className={`${getStatusColor(audit.status)} border font-medium px-2.5 py-0.5 text-[11px] rounded-full`}>{formatStatus(audit.status)}</Badge>
        </div>
        <h4 className={`${solo ? 'text-2xl' : 'text-xl'} font-bold text-[#0F172A] tracking-tight truncate`}>{audit.name}</h4>
        {audit.organization && <p className="text-sm text-[#64748B] mt-0.5 truncate">{audit.organization}</p>}
        {audit.frameworks?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {audit.frameworks.map((f) => (
              <Badge key={f} variant="outline" className="bg-[#F9FAFB] text-[#475569] border-[#E5E7EB] text-[11px] font-medium px-2 py-0.5 rounded-md">{f}</Badge>
            ))}
          </div>
        )}
      </div>
    );
    const created = (
      <div className="flex items-center gap-2 text-xs text-[#64748B]">
        <Calendar className="w-3.5 h-3.5" /> Created {formatDateTime(audit.created_at)}
      </div>
    );
    const continueBtn = (
      <Button onClick={() => navigate(`/audit/${audit.id}`)} className={`bg-[#0F62FE] text-white hover:bg-[#0D54D8] shadow-sm font-medium ${solo ? 'flex-1' : ''}`} data-testid={`continue-audit-${audit.id}`}>
        Continue Audit <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
    );
    const unpinBtn = (
      <Button onClick={(e) => togglePin(e, audit.id)} variant="outline" className="border-[#D7E0F2] text-[#64748B] hover:bg-[#F8FAFC] shrink-0" data-testid={`unpin-${audit.id}`} title="Unpin from Command Centre">
        <PinOff className="w-4 h-4" />
      </Button>
    );

    if (solo) {
      return (
        <Card className="bg-white" data-testid={`command-card-${audit.id}`}>
          <CardContent className="p-6">
            <div className="flex flex-col lg:flex-row gap-6">
              <div className="flex-1 min-w-0 space-y-5">
                {meta}
                {chips}
                {created}
              </div>
              <div className="lg:w-60 shrink-0 lg:border-l lg:border-[#F1F5F9] lg:pl-6 flex flex-col items-center gap-5">
                {ring}
                <div className="flex items-center gap-2 w-full">
                  {continueBtn}
                  {unpinBtn}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card className="bg-white" data-testid={`command-card-${audit.id}`}>
        <CardContent className="p-6">
          <div className="flex items-start gap-6">
            {meta}
            <div className="shrink-0">{ring}</div>
          </div>
          <div className="mt-5">{chips}</div>
          <div className="mt-4">{created}</div>
          <div className="flex items-center justify-between mt-4">
            {continueBtn}
            {unpinBtn}
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-appbg">
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F62FE]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-appbg" style={{ fontFamily: "'Geist', ui-sans-serif, system-ui, -apple-system, sans-serif" }}>
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Welcome */}
        <div className="mb-10 animate-fadeIn">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#0F172A] mb-2 tracking-tight">
            {(() => {
              const h = new Date().getHours();
              const greeting = h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
              return `${greeting}, ${user?.full_name?.split(' ')[0] || user?.email?.split('@')[0]}`;
            })()}
          </h2>
          <p className="text-base text-[#64748B]">Monitor compliance readiness across your audits and frameworks.</p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-8 animate-slideIn items-stretch">
            <div className="nova-card h-full bg-white rounded-2xl border border-[#D7E0F2] p-5">
              <div className="flex items-center justify-between mb-4">
                <p className="text-xs font-medium text-[#94A3B8] uppercase tracking-wide">Total Audits</p>
                <div className="w-9 h-9 rounded-xl bg-[#EFF6FF] flex items-center justify-center shrink-0"><FileText className="w-5 h-5 text-[#0F62FE]" /></div>
              </div>
              <p className="text-4xl font-bold text-[#0F172A] leading-none">{stats.total_audits}</p>
            </div>
            <div className="nova-card h-full bg-white rounded-2xl border border-[#D7E0F2] p-5">
              <div className="flex items-center justify-between mb-4">
                <p className="text-xs font-medium text-[#94A3B8] uppercase tracking-wide">In Progress</p>
                <div className="w-9 h-9 rounded-xl bg-[#EFF6FF] flex items-center justify-center shrink-0"><Clock className="w-5 h-5 text-[#0F62FE]" /></div>
              </div>
              <p className="text-4xl font-bold text-[#0F172A] leading-none">{stats.in_progress_audits}</p>
            </div>
            <div className="nova-card h-full bg-white rounded-2xl border border-[#D7E0F2] p-5">
              <div className="flex items-center justify-between mb-4">
                <p className="text-xs font-medium text-[#94A3B8] uppercase tracking-wide">Completed</p>
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${stats.completed_audits > 0 ? 'bg-[#ECFDF5]' : 'bg-[#EFF6FF]'}`}>
                  <CheckCircle className={`w-5 h-5 ${stats.completed_audits > 0 ? 'text-[#047857]' : 'text-[#0F62FE]'}`} />
                </div>
              </div>
              <p className="text-4xl font-bold text-[#0F172A] leading-none">{stats.completed_audits}</p>
            </div>
            <div className="nova-card h-full bg-white rounded-2xl border border-[#D7E0F2] p-5">
              <div className="flex items-center justify-between mb-4">
                <p className="text-xs font-medium text-[#94A3B8] uppercase tracking-wide">Overall Progress</p>
                <div className="w-9 h-9 rounded-xl bg-[#EFF6FF] flex items-center justify-center shrink-0"><TrendingUp className="w-5 h-5 text-[#0F62FE]" /></div>
              </div>
              <p className={`text-4xl font-bold leading-none ${Math.round(stats.overall_progress) > 0 ? 'text-[#0F172A]' : 'text-[#94A3B8]'}`}>{Math.round(stats.overall_progress)}%</p>
              <Progress value={stats.overall_progress} className={`h-1.5 mt-3 rounded-full ${Math.round(stats.overall_progress) > 0 ? 'bg-[#0F62FE]/12 [&>div]:bg-[#0F62FE]' : 'bg-[#E5E9F2] [&>div]:bg-[#CBD5E1]'}`} />
              <p className={`text-xs mt-2 font-medium ${Math.round(stats.overall_progress) > 0 ? 'text-[#047857]' : 'text-[#94A3B8]'}`}>{Math.round(stats.overall_progress) > 0 ? 'In progress' : 'Getting Started'}</p>
            </div>
          </div>
        )}

        <CompanyNameOnboarding />

        {/* Command Centre */}
        <div className="flex items-center justify-between mb-5 mt-12 animate-slideIn">
          <div>
            <h3 className="text-2xl sm:text-[28px] font-bold text-[#0F172A] tracking-tight">Audit Command Centre</h3>
            <p className="text-sm text-[#64748B] mt-1">Pinned audits in focus — readiness, attention areas, and next steps.</p>
          </div>
          <Button onClick={() => navigate('/audit/new')} variant="outline" className="bg-white border-[#0F62FE] text-[#0F62FE] hover:bg-[#EFF6FF] font-medium" data-testid="new-audit-button">
            <Plus className="w-5 h-5 mr-2" /> New Audit
          </Button>
        </div>

        {pinnedAudits.length === 0 ? (
          <Card className="bg-white animate-slideIn" data-testid="command-centre-empty">
            <CardContent className="p-10 text-center">
              <div className="w-12 h-12 rounded-2xl bg-[#EFF6FF] flex items-center justify-center mx-auto mb-3"><Pin className="w-5 h-5 text-[#0F62FE]" /></div>
              <h4 className="text-base font-bold text-[#0F172A]">No audits pinned yet</h4>
              <p className="text-sm text-[#64748B] mt-1">Pin one or more audits from the table below to track their readiness here.</p>
            </CardContent>
          </Card>
        ) : (
          <div className={pinnedAudits.length === 1 ? 'animate-slideIn' : 'grid gap-5 animate-slideIn grid-cols-1 xl:grid-cols-2'}>
            {pinnedAudits.length === 1
              ? renderPinnedCard(pinnedAudits[0], true)
              : pinnedAudits.map((audit) => (
                  <React.Fragment key={audit.id}>{renderPinnedCard(audit, false)}</React.Fragment>
                ))}
          </div>
        )}

        {/* All Audits table */}
        <div className="mt-12 animate-slideIn">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-5">
            <div>
              <h3 className="text-2xl font-bold text-[#0F172A] tracking-tight">All Audits</h3>
              <p className="text-sm text-[#64748B] mt-1">Browse, sort, and pin audits to your Command Centre.</p>
            </div>
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
              <Input value={auditSearch} onChange={(e) => setAuditSearch(e.target.value)} placeholder="Search audits…" className="pl-9 bg-white border-[#D7E0F2]" data-testid="audit-search" />
            </div>
          </div>

          {/* Tabs + bulk bar */}
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <div className="inline-flex items-center gap-1 bg-white border border-[#D7E0F2] rounded-lg p-1">
              {[
                { key: 'active', label: 'Active', count: activeCount },
                { key: 'archived', label: 'Archived', count: archivedCount },
                { key: 'all', label: 'All', count: audits.length },
              ].map(t => (
                <button key={t.key} onClick={() => setActiveTab(t.key)} data-testid={`tab-${t.key}`}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${activeTab === t.key ? 'bg-[#0F62FE] text-white' : 'text-[#64748B] hover:bg-[#F1F5F9]'}`}>
                  {t.label}<span className={`ml-1.5 text-xs ${activeTab === t.key ? 'text-white/80' : 'text-[#94A3B8]'}`}>{t.count}</span>
                </button>
              ))}
            </div>
            {selectedIds.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-[#64748B]">{selectedIds.length} selected</span>
                {activeTab !== 'archived' && (
                  <Button size="sm" variant="outline" className="border-[#D7E0F2]" onClick={() => openBulkDialog('archive')} data-testid="bulk-archive-btn"><Archive className="w-4 h-4 mr-1.5" /> Archive</Button>
                )}
                {activeTab !== 'active' && (
                  <Button size="sm" variant="outline" className="border-[#D7E0F2]" onClick={() => openBulkDialog('unarchive')} data-testid="bulk-unarchive-btn"><ArchiveRestore className="w-4 h-4 mr-1.5" /> Unarchive</Button>
                )}
              </div>
            )}
          </div>

          <Card className="bg-white overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="audits-table">
                <thead>
                  <tr className="border-b border-[#E5E9F2] bg-[#F8FAFC]">
                    <th className="w-10 px-4 py-3 text-left">
                      <Checkbox checked={allSelected} onCheckedChange={toggleSelectAll} data-testid="select-all" />
                    </th>
                    <th className="px-4 py-3 text-left">
                      <button onClick={() => toggleSort('name')} className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#64748B] uppercase tracking-wide">Audit <SortIcon col="name" /></button>
                    </th>
                    <th className="px-4 py-3 text-left hidden md:table-cell">
                      <button onClick={() => toggleSort('organization')} className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#64748B] uppercase tracking-wide">Organization <SortIcon col="organization" /></button>
                    </th>
                    <th className="px-4 py-3 text-left hidden lg:table-cell"><span className="text-xs font-semibold text-[#64748B] uppercase tracking-wide">Frameworks</span></th>
                    <th className="px-4 py-3 text-left">
                      <button onClick={() => toggleSort('status')} className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#64748B] uppercase tracking-wide">Status <SortIcon col="status" /></button>
                    </th>
                    <th className="px-4 py-3 text-left">
                      <button onClick={() => toggleSort('progress')} className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#64748B] uppercase tracking-wide">Progress <SortIcon col="progress" /></button>
                    </th>
                    <th className="px-4 py-3 text-left hidden lg:table-cell">
                      <button onClick={() => toggleSort('created_at')} className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#64748B] uppercase tracking-wide">Created <SortIcon col="created_at" /></button>
                    </th>
                    <th className="px-4 py-3 text-right"><span className="text-xs font-semibold text-[#64748B] uppercase tracking-wide">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAudits.length === 0 ? (
                    <tr><td colSpan={8} className="px-4 py-12 text-center text-sm text-[#94A3B8]">No audits found.</td></tr>
                  ) : filteredAudits.map((audit) => {
                    const pct = Math.round(audit.progress || 0);
                    const isPinned = pinnedIds.includes(audit.id);
                    return (
                      <tr key={audit.id} className="border-b border-[#F1F5F9] hover:bg-[#F8FAFC] transition-colors cursor-pointer" onClick={() => navigate(`/audit/${audit.id}`)} data-testid={`audit-row-${audit.id}`}>
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <Checkbox checked={selectedIds.includes(audit.id)} onCheckedChange={() => toggleSelect(audit.id)} data-testid={`select-${audit.id}`} />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 min-w-0">
                            {isPinned && <Pin className="w-3.5 h-3.5 text-[#0F62FE] shrink-0 fill-[#0F62FE]" />}
                            <div className="min-w-0">
                              <p className="font-semibold text-[#0F172A] truncate max-w-[260px]">{audit.name}</p>
                              <p className="font-mono text-[11px] text-[#94A3B8]">{audit.audit_ref || 'No ID'}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell text-[#475569] truncate max-w-[180px]">{audit.organization || '—'}</td>
                        <td className="px-4 py-3 hidden lg:table-cell">
                          <div className="flex flex-wrap gap-1">
                            {(audit.frameworks || []).slice(0, 3).map(f => (
                              <Badge key={f} variant="outline" className="bg-[#F9FAFB] text-[#475569] border-[#E5E7EB] text-[10px] font-medium px-1.5 py-0 rounded">{f}</Badge>
                            ))}
                            {(audit.frameworks || []).length > 3 && <span className="text-[10px] text-[#94A3B8]">+{audit.frameworks.length - 3}</span>}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={`${getStatusColor(audit.status)} border font-medium px-2 py-0.5 text-[11px] rounded-full`}>{formatStatus(audit.status)}</Badge>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 min-w-[120px]">
                            <div className="flex-1 h-1.5 rounded-full bg-[#E5E9F2] overflow-hidden">
                              <div className="h-full bg-[#0F62FE] rounded-full" style={{ width: `${pct}%` }} />
                            </div>
                            <span className="text-xs font-medium text-[#64748B] w-9 text-right">{pct}%</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell text-xs text-[#94A3B8] whitespace-nowrap">{formatDateTime(audit.created_at)}</td>
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-1">
                            <Button size="sm" variant="ghost" onClick={(e) => togglePin(e, audit.id)} className={`h-8 px-2 ${isPinned ? 'text-[#0F62FE]' : 'text-[#94A3B8] hover:text-[#0F62FE]'}`} data-testid={`pin-toggle-${audit.id}`} title={isPinned ? 'Unpin from Command Centre' : 'Pin to Command Centre'}>
                              {isPinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
                            </Button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button size="sm" variant="ghost" className="h-8 px-2 text-[#94A3B8]" data-testid={`audit-menu-${audit.id}`}><MoreVertical className="w-4 h-4" /></Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => navigate(`/audit/${audit.id}`)}>Open</DropdownMenuItem>
                                {audit.archived_at ? (
                                  <DropdownMenuItem onClick={() => handleUnarchive(audit.id)}><ArchiveRestore className="w-4 h-4 mr-2" /> Unarchive</DropdownMenuItem>
                                ) : (
                                  <DropdownMenuItem onClick={() => handleArchive(audit.id)}><Archive className="w-4 h-4 mr-2" /> Archive</DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      </div>

      {/* Bulk Confirm Dialog */}
      <AlertDialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
        <AlertDialogContent data-testid="audit-bulk-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{bulkAction === 'archive' ? 'Archive Selected Audits?' : 'Unarchive Selected Audits?'}</AlertDialogTitle>
            <AlertDialogDescription>
              {bulkAction === 'archive'
                ? `You are about to archive ${selectedIds.length} audit(s). Archived audits can be restored later.`
                : `You are about to unarchive ${selectedIds.length} audit(s). They will reappear in the Active list.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="audit-bulk-dialog-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkConfirm} disabled={bulkProcessing} data-testid="audit-bulk-dialog-confirm">
              {bulkProcessing ? 'Processing...' : bulkAction === 'archive' ? 'Archive' : 'Unarchive'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Dashboard;
